import { useState, useEffect, useRef, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useToast } from '@/hooks/useToast'
import { Logo } from '@/components/Logo'

declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, options: Record<string, unknown>) => string
      reset: (widgetId: string) => void
      remove: (widgetId: string) => void
    }
  }
}

export function HomePage() {
  const [url, setUrl] = useState('')
  const [shortUrl, setShortUrl] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [captchaToken, setCaptchaToken] = useState('')
  const captchaRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)
  const { showToast } = useToast()

  const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY

  const renderTurnstile = useCallback(() => {
    if (!TURNSTILE_SITE_KEY || !window.turnstile || !captchaRef.current) return
    if (widgetIdRef.current) {
      window.turnstile.remove(widgetIdRef.current)
    }
    widgetIdRef.current = window.turnstile.render(captchaRef.current, {
      sitekey: TURNSTILE_SITE_KEY,
      callback: (token: string) => setCaptchaToken(token),
      'expired-callback': () => setCaptchaToken(''),
      theme: 'dark',
      size: 'flexible',
      appearance: 'interaction-only',
    })
  }, [TURNSTILE_SITE_KEY])

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return

    // Load Turnstile script if not already loaded
    if (document.querySelector('script[src*="turnstile"]')) {
      renderTurnstile()
      return
    }

    const script = document.createElement('script')
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad'
    script.async = true
      ; (window as unknown as Record<string, unknown>).onTurnstileLoad = renderTurnstile
    document.head.appendChild(script)
  }, [TURNSTILE_SITE_KEY, renderTurnstile])

  const handleShorten = async () => {
    if (!url.trim()) {
      setError('Please enter a URL')
      return
    }

    if (TURNSTILE_SITE_KEY && !captchaToken) {
      setError('Please complete the CAPTCHA verification')
      return
    }

    setLoading(true)
    setError('')
    setShortUrl('')

    try {
      const res = await fetch('/api/public/shorten', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url, captchaToken: captchaToken || undefined })
      })

      const data = await res.json()

      if (!res.ok) {
        throw new Error(data.error || 'Failed to shorten URL')
      }

      setShortUrl(data.shortUrl)
      // Reset captcha after success
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current)
        setCaptchaToken('')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong')
    } finally {
      setLoading(false)
    }
  }

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(shortUrl)
    showToast('Link copied to clipboard!')
  }

  const originalLength = url.length
  const shortLength = shortUrl.length
  const savedPercent = originalLength > 0 ? Math.round((1 - shortLength / originalLength) * 100) : 0

  return (
    <div className="min-h-screen bg-[#f6f6f8] dark:bg-[#111621] text-[#0e121b] dark:text-white">
      {/* Floating NavBar */}
      <div className="fixed top-6 left-0 right-0 z-50 flex justify-center px-4">
        <header className="flex items-center justify-between w-full max-w-[960px] bg-white/80 dark:bg-[#1c2230]/80 backdrop-blur-md border border-gray-200 dark:border-gray-800 rounded-full px-6 py-2.5 shadow-sm">
          <Link to="/">
            <Logo size="md" variant="auto" />
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <a className="text-sm font-medium hover:text-[#1754cf] transition-colors" href="#features">Features</a>
            <Link className="text-sm font-medium hover:text-[#1754cf] transition-colors" to="/pricing">Pricing</Link>
            <Link className="text-sm font-medium hover:text-[#1754cf] transition-colors" to="/login">Login</Link>
          </nav>
          <Link
            to="/signup"
            className="bg-[#1754cf] text-white text-sm font-bold px-5 py-2 rounded-full hover:opacity-90 transition-opacity"
          >
            Get Started
          </Link>
        </header>
      </div>

      <main className="pt-32">
        {/* Hero Section */}
        <section className="max-w-[960px] mx-auto px-6 py-16 text-center">
          <div className="flex flex-col items-center gap-6">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1754cf]/10 border border-[#1754cf]/20 w-fit">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#1754cf] opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#1754cf]" />
              </span>
              <span className="text-xs font-bold text-[#1754cf] tracking-wide uppercase">New: API v2.0 Available</span>
            </div>

            {/* Headline */}
            <h1 className="text-5xl md:text-7xl font-black tracking-[-0.05em] leading-[1.1] max-w-[800px]">
              The power of a short link, <span className="text-[#1754cf]">amplified</span>
            </h1>

            <p className="text-gray-500 dark:text-gray-400 text-lg md:text-xl max-w-[600px]">
              Experience the fastest way to shorten, track, and amplify your links with real-time analytics.
            </p>

            {/* URL Shortener Widget */}
            <div className="w-full max-w-xl mt-8 bg-white dark:bg-[#1c2230] p-2 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-2">
                <div className="flex-1 relative flex items-center">
                  <span className="material-symbols-outlined absolute left-4 text-gray-400">link</span>
                  <input
                    value={url}
                    onChange={e => setUrl(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleShorten()}
                    className="w-full pl-12 pr-4 py-4 bg-transparent border-none focus:ring-0 text-lg placeholder:text-gray-400 dark:text-white focus:outline-none"
                    placeholder="Paste your long URL here..."
                    type="url"
                  />
                </div>
                <button
                  onClick={handleShorten}
                  disabled={loading}
                  className="bg-[#1754cf] text-white font-bold px-8 py-4 rounded-xl hover:scale-[1.02] active:scale-95 transition-all flex items-center gap-2 disabled:opacity-50"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <span>Shorten</span>
                  )}
                </button>
              </div>

              {/* Turnstile CAPTCHA */}
              {TURNSTILE_SITE_KEY && (
                <div className="px-4 pt-2" ref={captchaRef} />
              )}

              {/* Error Message */}
              {error && (
                <div className="px-4 py-2 text-sm text-red-500 font-medium">{error}</div>
              )}

              {/* Result Preview */}
              {shortUrl && (
                <div className="mt-2 p-4 border-t border-gray-50 dark:border-gray-800 bg-[#1754cf]/5 rounded-b-xl animate-slideUp">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="size-8 bg-[#1754cf]/10 rounded flex items-center justify-center text-[#1754cf]">
                        <span className="material-symbols-outlined text-[20px]">bolt</span>
                      </div>
                      <div className="text-left">
                        <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400">Your Short Link</p>
                        <a href={shortUrl} target="_blank" rel="noopener noreferrer" className="font-bold text-[#1754cf] hover:underline">
                          {shortUrl.replace('http://', '').replace('https://', '')}
                        </a>
                      </div>
                    </div>
                    <button
                      onClick={copyToClipboard}
                      className="text-sm font-semibold flex items-center gap-1 text-[#1754cf] hover:bg-[#1754cf]/10 px-3 py-2 rounded-lg transition-colors"
                    >
                      <span className="material-symbols-outlined text-sm">content_copy</span>
                      <span>Copy</span>
                    </button>
                  </div>

                  {/* Stats */}
                  <div className="mt-4 flex items-center justify-center gap-6 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400">Original:</span>
                      <span className="font-bold text-gray-600">{originalLength}</span>
                      <span className="text-gray-400">chars</span>
                    </div>
                    <span className="text-gray-300">→</span>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400">Short:</span>
                      <span className="font-bold text-[#1754cf]">{shortLength}</span>
                      <span className="text-gray-400">chars</span>
                    </div>
                    <div className="flex items-center gap-2 text-green-600">
                      <span className="material-symbols-outlined text-sm">trending_down</span>
                      <span className="font-bold">{savedPercent}%</span>
                      <span>smaller</span>
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 text-center">
                    <p className="text-sm text-gray-500 mb-2">Sign up to save and track this link</p>
                    <Link to="/signup" className="inline-flex items-center gap-2 text-sm font-bold text-[#1754cf] hover:underline">
                      Create Free Account <span className="material-symbols-outlined text-sm">arrow_forward</span>
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Trust Badges */}
            <div className="flex items-center gap-6 text-xs text-gray-400 font-medium mt-4">
              <span className="flex items-center gap-1">
                <span className="material-symbols-outlined text-[14px]">check_circle</span> No credit card required
              </span>
              <span className="flex items-center gap-1">
                <span className="material-symbols-outlined text-[14px]">check_circle</span> 100% Free to start
              </span>
            </div>
          </div>
        </section>

        {/* Social Proof */}
        <div className="max-w-[960px] mx-auto px-6 py-12 opacity-40 grayscale flex flex-wrap justify-center gap-12 items-center">
          <span className="font-bold text-xl tracking-tighter">TECHFLOW</span>
          <span className="font-bold text-xl tracking-tighter">PRISM.INC</span>
          <span className="font-bold text-xl tracking-tighter">VELOCITY</span>
          <span className="font-bold text-xl tracking-tighter">DATACORE</span>
        </div>

        {/* Features Section */}
        <section className="max-w-[960px] mx-auto px-6 py-20" id="features">
          <div className="mb-12">
            <h2 className="text-3xl font-bold tracking-tight mb-4">Fast and Efficient</h2>
            <p className="text-gray-500 dark:text-gray-400 max-w-lg">
              Our suite of tools is designed for maximum performance and reliability.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <FeatureCard
              icon="monitoring"
              title="Real-time Analytics"
              description="Track clicks, geographic data, and referrers as they happen."
            />
            <FeatureCard
              icon="bolt"
              title="Lightning Fast"
              description="Sub-millisecond redirects powered by edge computing."
            />
            <FeatureCard
              icon="lock"
              title="Secure by Default"
              description="Enterprise-grade security with SOC2 compliance."
            />
          </div>
        </section>

        {/* CTA Section */}
        <section className="max-w-[960px] mx-auto px-6 py-20 text-center">
          <h2 className="text-3xl font-bold tracking-tight mb-4">Ready to get started?</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">
            Join thousands of teams using URLzippy to power their links.
          </p>
          <Link
            to="/signup"
            className="inline-flex items-center gap-2 bg-[#1754cf] text-white font-bold px-8 py-4 rounded-xl hover:opacity-90 transition-opacity"
          >
            Start for Free
            <span className="material-symbols-outlined">arrow_forward</span>
          </Link>
        </section>

        {/* Footer */}
        <footer className="max-w-[960px] mx-auto px-6 py-12 border-t border-gray-200 dark:border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <Logo size="sm" variant="auto" />
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="text-sm text-gray-400 hover:text-[#1754cf] transition-colors">Privacy Policy</Link>
              <Link to="/pricing" className="text-sm text-gray-400 hover:text-[#1754cf] transition-colors">Pricing</Link>
            </div>
            <p className="text-sm text-gray-400">© {new Date().getFullYear()} URLzippy. All rights reserved.</p>
          </div>
        </footer>
      </main>
    </div>
  )
}

function FeatureCard({ icon, title, description }: { icon: string; title: string; description: string }) {
  return (
    <div className="p-8 border border-gray-200 dark:border-gray-800 rounded-2xl bg-white dark:bg-[#1c2230] hover:border-[#1754cf] transition-colors group">
      <span className="material-symbols-outlined text-[#1754cf] mb-4 text-3xl group-hover:scale-110 transition-transform block">
        {icon}
      </span>
      <h3 className="text-lg font-bold mb-2">{title}</h3>
      <p className="text-gray-500 dark:text-gray-400 text-sm">{description}</p>
    </div>
  )
}
